import { useMoneyFormat } from '@arabat/web-core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaPicker } from '../pages/AreaPicker.js';
import { useDeliveryChoice, useLocalName, useStorefront } from '../shared.js';
import { isPreview } from './context.js';

/**
 * Asking for the delivery area on entering the store (ADR-0141), when the merchant chose it:
 * a sheet with the governorates and their areas, "Pickup from the branch" and "Browse without
 * choosing". Once chosen, a line under the header shows the area with "Change"; the stock of
 * the area's branch shows and the checkout starts with the area filled in. The branch itself is
 * never named.
 */
export function AreaPrompt() {
  const { data: store } = useStorefront();
  const { choice, setChoice, dismissed, dismiss } = useDeliveryChoice();
  const [open, setOpen] = useState(false);
  const active =
    store !== undefined &&
    store.areaPrompt === 'entry' &&
    store.deliveryEnabled &&
    store.deliveryAreas.length > 0 &&
    !isPreview();
  if (!active) return null;
  const known =
    choice?.mode === 'pickup' ||
    (choice?.mode === 'delivery' && store.deliveryAreas.some((a) => a.id === choice.areaId));
  const asking = open || (!known && !dismissed);
  return (
    <>
      {known ? <ChoiceLine onChange={() => setOpen(true)} /> : null}
      {asking ? (
        <AreaSheet
          onClose={() => {
            setOpen(false);
            if (!known) dismiss();
          }}
          onDelivery={(areaId, placeId) => {
            setChoice({ mode: 'delivery', areaId, placeId });
            setOpen(false);
          }}
          onPickup={
            store.pickupEnabled
              ? () => {
                  setChoice({ mode: 'pickup' });
                  setOpen(false);
                }
              : null
          }
        />
      ) : null}
    </>
  );
}

/** "📍 Salmiya, Hawalli · Delivery KWD 1.000 — Change" under the header. */
function ChoiceLine({ onChange }: { onChange: () => void }) {
  const { data: store } = useStorefront();
  const { choice } = useDeliveryChoice();
  const { t, i18n } = useTranslation();
  const name = useLocalName();
  const money = useMoneyFormat();
  if (!store || !choice) return null;
  let label = t('areaPrompt.pickup');
  if (choice.mode === 'delivery') {
    const zone = store.deliveryAreas.find((a) => a.id === choice.areaId);
    const place = zone?.places.find((p) => p.id === choice.placeId);
    const region = place ? store.deliveryRegions.find((r) => r.id === place.regionId) : undefined;
    const where = place
      ? [place, ...(region ? [region] : [])]
          .map(name)
          .join(i18n.language.startsWith('ar') ? '، ' : ', ')
      : zone
        ? name(zone)
        : '';
    label = zone ? `${where} · ${t('areaPrompt.fee', { fee: money(zone.fee) })}` : where;
  }
  return (
    <div className="border-b border-border bg-surface" data-testid="area-line">
      <button
        type="button"
        onClick={onChange}
        className="mx-auto flex w-full max-w-6xl items-center gap-2 px-4 py-2 text-start text-sm"
      >
        <span aria-hidden>{choice.mode === 'delivery' ? '📍' : '🏪'}</span>
        <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
        <span className="font-semibold text-primary">{t('checkout.change')}</span>
      </button>
    </div>
  );
}

function AreaSheet({
  onClose,
  onDelivery,
  onPickup,
}: {
  onClose: () => void;
  onDelivery: (areaId: string, placeId: string | null) => void;
  onPickup: (() => void) | null;
}) {
  const { data: store } = useStorefront();
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLElement>('input')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = before;
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  if (!store) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal
      aria-label={t('areaPrompt.title')}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('checkout.closeSheet')}
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panel}
        className="sheet-up relative flex max-h-[88dvh] w-full flex-col gap-3 overflow-y-auto rounded-t-2xl bg-canvas p-4 shadow-xl sm:max-w-lg sm:rounded-2xl"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto h-1.5 w-10 shrink-0 rounded-full bg-border sm:hidden" aria-hidden />
        <h2 className="text-lg font-semibold">{t('areaPrompt.title')}</h2>
        <AreaPicker
          zones={store.deliveryAreas}
          regions={store.deliveryRegions}
          value={{ areaId: null, placeId: null }}
          onChange={(c) => onDelivery(c.areaId, c.placeId)}
        />
        {onPickup ? (
          <button
            type="button"
            onClick={onPickup}
            className="rounded-xl border border-border bg-surface px-4 py-3 text-start font-semibold hover:border-primary/50"
          >
            🏪 {t('areaPrompt.pickup')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          className="py-2 text-center text-sm text-fg-muted underline"
        >
          {t('areaPrompt.browse')}
        </button>
      </div>
    </div>
  );
}
